// src/services/pickerCompletion.ts
// -------------------------------
// The two extra things a picker's "Completed" does beyond saving cost/order-no and
// releasing the SO (John, 26 Jul 2026):
//   (1) appendOrderNumbersToSoLines — stamp the picker's order number onto each of
//       the SO's line comments (request_sales_orders.line_comments), per line, so it
//       flows to Unleashed. Idempotent (a re-complete replaces, never doubles).
//   (2) postOrderConfirmationOnce — send the DFV Service the editable
//       "Pickers / Order Confirmation" comment + email, EXACTLY ONCE per request per
//       item-type, via an ATOMIC claim (db-165 request_picker_notifications) — so a
//       picker completing many SOs of the same request can't flood the service.
// Both are BEST-EFFORT — they never throw, so a failure can't block the completion.

import { listCommentTemplates } from "../data/commentTemplates";
import { claimPickerNotification } from "../data/pickerNotifications";
import { appendRequestNotes } from "../data/requests";
import {
  getSalesOrderByNumber,
  updateSalesOrderLineComment,
  updateSalesOrderLinePrice,
  updateSalesOrderLineProductCode,
} from "../data/salesOrders";
import { sendEmail } from "../utils/actions";
import { logger } from "../utils/logger";
import { sanitizeText } from "../utils/sanitize";

// Strips a previously-appended "— Order No. - …" tail so a re-complete replaces it.
const ORDER_NO_TAIL = /\s*—\s*Order No\. -.*$/;

// (1) Append each item's order number to its SO line comment. `itemOrderNumbers` is
// in the SAME order as the SO's non-DISCOUNT lines (child order). Best-effort.
export async function appendOrderNumbersToSoLines(
  orderNumber: string | null | undefined,
  itemOrderNumbers: (string | null | undefined)[]
): Promise<void> {
  if (!orderNumber) return;
  try {
    const so = await getSalesOrderByNumber(orderNumber);
    if (!so) return;
    const lines = (so.lines || []).filter((l) => l.product_code && l.product_code !== "DISCOUNT");
    for (let i = 0; i < lines.length; i++) {
      const orderNo = String(itemOrderNumbers[i] ?? "").trim();
      if (!orderNo) continue;
      const base = String(lines[i].line_comments ?? "").replace(ORDER_NO_TAIL, "");
      const comment = base ? `${base} — Order No. - ${orderNo}` : `Order No. - ${orderNo}`;
      await updateSalesOrderLineComment(lines[i].id, comment);
    }
  } catch (e) {
    logger.warn("appendOrderNumbersToSoLines error:", e);
  }
}

// Update each SO line's unit price to the picker's ACTUAL cost. `itemAmounts` is in
// the SAME order as the SO's non-DISCOUNT lines (child order) — the same mapping
// appendOrderNumbersToSoLines relies on. unit_price is set so the line TOTAL
// (order_quantity × unit_price) equals the entered amount (for these qty-1 picker
// lines that's just the amount). Best-effort — never throws.
export async function updateSoLinePrices(
  orderNumber: string | null | undefined,
  itemAmounts: (number | string | null | undefined)[]
): Promise<void> {
  if (!orderNumber) return;
  try {
    const so = await getSalesOrderByNumber(orderNumber);
    if (!so) return;
    const lines = (so.lines || []).filter((l) => l.product_code && l.product_code !== "DISCOUNT");
    for (let i = 0; i < lines.length; i++) {
      const amt = Number(itemAmounts[i]);
      if (!Number.isFinite(amt) || amt <= 0) continue;
      const qty = Number(lines[i].order_quantity) || 1;
      const unit = amt / qty;
      if (unit === Number(lines[i].unit_price)) continue;
      await updateSalesOrderLinePrice(lines[i].id, unit);
    }
  } catch (e) {
    logger.warn("updateSoLinePrices error:", e);
  }
}

// Update each SO line's product_code to the picker's chosen Unleashed code.
// `itemCodes` is in the SAME order as the SO's non-DISCOUNT lines (child order) — the
// same mapping updateSoLinePrices / appendOrderNumbersToSoLines rely on. A blank or
// unchanged code is skipped (keeps the mapped placeholder). Best-effort — never throws.
export async function updateSoLineProductCodes(
  orderNumber: string | null | undefined,
  itemCodes: (string | null | undefined)[]
): Promise<void> {
  if (!orderNumber) return;
  try {
    const so = await getSalesOrderByNumber(orderNumber);
    if (!so) return;
    const lines = (so.lines || []).filter((l) => l.product_code && l.product_code !== "DISCOUNT");
    for (let i = 0; i < lines.length; i++) {
      const code = String(itemCodes[i] ?? "").trim();
      if (!code || code === lines[i].product_code) continue;
      await updateSalesOrderLineProductCode(lines[i].id, code);
    }
  } catch (e) {
    logger.warn("updateSoLineProductCodes error:", e);
  }
}

const OC_TEMPLATE_ID = "p01";
const OC_FALLBACK = "Your product has been ordered .....";

interface OcReq {
  id: number | string;
  refNo?: string | null;
  shelterName?: string | null;
  shelterEmail?: string | null;
  notes?: unknown[] | null;
}

// Fill the template tokens the Order Confirmation supports (docs: {{item_type}} =
// "Stationery"/"Device"; {{ref_no}}; {{dfv_service}}). Unknown tokens are left as-is.
function renderOcTokens(
  template: string,
  values: { item_type?: string | null; ref_no?: string | null; dfv_service?: string | null }
): string {
  const map: Record<string, string> = {
    item_type: values.item_type ?? "",
    ref_no: values.ref_no ?? "",
    dfv_service: values.dfv_service ?? "",
  };
  return String(template ?? "").replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, k) =>
    k in map ? map[k] : m
  );
}

// (2) Post the "Order Confirmation" comment to the DFV Service + send the editable Picker
// email, EXACTLY ONCE per request per item-type. The dedup is ATOMIC (db-165): the first
// completion of this type CLAIMS the notification; the rest are silent no-ops. This replaces
// the old read-modify-write marker in the request notes, which raced across a picker's rapid
// back-to-back completions (a top-up's many packs, a request's children) and flooded the
// service with 20 / 5 copies. `itemType` = "Stationery" | "Device" | "Stock Top-up" — fills
// the {{item_type}} token + the claim key. Returns { posted }. Best-effort — never throws.
export async function postOrderConfirmationOnce(
  req: OcReq,
  authorName?: string | null,
  itemType?: string | null
): Promise<{ posted: boolean }> {
  try {
    const type = itemType || "";
    // Atomic claim — only the FIRST completion of this request+type wins (db-165). No
    // read-modify-write on the notes array, so rapid back-to-back completions can't dupe.
    const claimed = await claimPickerNotification(req.id, type, authorName);
    if (!claimed) return { posted: false };

    // The (editable) template text, by id then by section/title, else the default.
    const res = await listCommentTemplates(false);
    const rows = res.data || [];
    const tpl =
      rows.find((t) => t.id === OC_TEMPLATE_ID) ||
      rows.find((t) => t.category === "Pickers" && t.title === "Order Confirmation");
    const text = renderOcTokens(tpl?.text || OC_FALLBACK, {
      item_type: type,
      ref_no: req.refNo,
      dfv_service: req.shelterName,
    });

    // Post the visible comment to the DFV Service. No dedup markers needed now — the claim
    // above enforces once-per-request-per-type. A comment-write failure is best-effort: the
    // email (the primary notification) still goes, since the claim is already spent.
    const note = {
      author: sanitizeText(authorName || "Zephyr"),
      text,
      date: new Date().toLocaleString("en-AU"),
      // Sortable ISO timestamp — the Comments thread orders by `ts` (falling back to
      // parsing `date`). Without it this note parsed to sortTs 0 and sank to the
      // bottom of the thread instead of the top (newest). Every other comment path
      // sets this. (2 Sep 2026 fix.)
      ts: new Date().toISOString(),
      byRole: "staff",
      // Thread colour tier — render like a responder's comment (teal), matching the
      // other staff comments, rather than the byRole fallback.
      tier: "responder",
    };
    // Atomic append (db-200) — never a whole-array rewrite from a browser copy.
    // Best-effort: the email is the primary notification, so a write failure is logged
    // only. Only a 'combined' user can write here — a pure picker is denied by the RPC,
    // exactly as by the requests UPDATE policy before. markUnread flags the shelter;
    // updated_by_role is derived server-side. docs/notes-atomic-append-plan.md.
    const { error } = await appendRequestNotes(req.id, [note], {
      updatedBy: sanitizeText(authorName || "Zephyr"),
      markUnread: true,
    });
    if (error) logger.warn("postOrderConfirmationOnce comment update failed:", error.message);
    if (req.shelterEmail) {
      // Pickers have their OWN editable notification email (Manage → System Content →
      // Automatic Emails → "Picker Order Confirmation"), not the generic New Comment one.
      sendEmail("picker_order_confirmation", {
        requestId: req.id,
        refNo: req.refNo,
        shelterName: req.shelterName,
        shelterEmail: req.shelterEmail,
        itemType: type,
      });
    }
    return { posted: true };
  } catch (e) {
    logger.warn("postOrderConfirmationOnce error:", e);
    return { posted: false };
  }
}
